import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useAuth } from '../../../../context/AuthContext'
import { useOrgan } from '../../../../hooks/useOrgan'
import useBaseService from '../../../../hooks/services/useBaseService'
import useNotification from '../../../../hooks/useNotification'
import { entities } from '../../../../constants/entities'
import {
  clearMovementDraft,
  clearMovementSessionExpired,
  getMovementDraftForRecovery,
  getMovementDraftKey,
  wasMovementSessionExpired,
} from '../utils/movementDraftStorage'

const MovementDraftRecoveryDialog = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectedOrgan } = useOrgan()
  const { post: baseServicePost } = useBaseService(navigate)
  const { showNotification } = useNotification()
  const [pendingMovementDraft, setPendingMovementDraft] = useState(null)
  const [discarding, setDiscarding] = useState(false)

  useEffect(() => {
    if (!wasMovementSessionExpired() || !user?.id || !selectedOrgan?.id) return

    const draft = getMovementDraftForRecovery(user.id, selectedOrgan.id)
    if (!draft) return

    if (draft.expired) {
      Promise.allSettled(
        (draft.reservedEquipments ?? []).map((equipmentId) =>
          baseServicePost(
            `${entities.equipments.base}/${equipmentId}/release-loading`,
            {},
            { skipNotification: true }
          )
        )
      ).finally(clearMovementSessionExpired)
      return
    }

    setPendingMovementDraft({
      key: getMovementDraftKey(user.id, selectedOrgan.id),
      data: draft,
    })
  }, [user?.id, selectedOrgan?.id, baseServicePost])

  const resumeDraft = () => {
    const serviceOrderId = pendingMovementDraft?.data?.serviceOrderId
    clearMovementSessionExpired()
    navigate(
      serviceOrderId
        ? `/aluguel/movimentos/criar?service_order_id=${encodeURIComponent(serviceOrderId)}`
        : '/aluguel/movimentos/criar'
    )
  }

  const discardDraft = async () => {
    if (!pendingMovementDraft) return

    setDiscarding(true)
    const results = await Promise.allSettled(
      (pendingMovementDraft.data.reservedEquipments ?? []).map((equipmentId) =>
        baseServicePost(
          `${entities.equipments.base}/${equipmentId}/release-loading`,
          {},
          { skipNotification: true }
        )
      )
    )

    if (results.some((result) => result.status === 'rejected')) {
      setDiscarding(false)
      showNotification(
        'error',
        'Não foi possível liberar todos os equipamentos. O rascunho foi mantido para nova tentativa.'
      )
      return
    }

    clearMovementDraft(pendingMovementDraft.key)
    clearMovementSessionExpired()
    setPendingMovementDraft(null)
    setDiscarding(false)
  }

  return (
    <Dialog
      open={!!pendingMovementDraft}
      onClose={() => {}}
      aria-labelledby="pending-movement-title">
      <DialogTitle id="pending-movement-title">
        Existe um movimento pendente
      </DialogTitle>
      <DialogContent>
        <Typography>
          Encontramos um rascunho salvo com equipamentos bipados. Deseja
          prosseguir de onde parou?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={discardDraft} disabled={discarding}>
          Descartar rascunho
        </Button>
        <Button variant="contained" onClick={resumeDraft} disabled={discarding}>
          Prosseguir
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MovementDraftRecoveryDialog
